import React, { useState, useEffect, useRef } from "react";
import {
  Wrapper,
  DropdownContainer,
  SelectedItem,
  SubText,
  DropdownButton,
  Title,
  HospitalSearchDropdown,
  IconContainer,
  EllipsisContainer,
  AddHospitalDiv,
  SearchWrapper,
  PartnerLocationIcon,
} from "./index.sc";
import HospitalSearchList from "../OptionsList/HospitalSearchList";
import { ReactComponent as DropdownSearch } from "../../Assets/Icons/DropdownSearch.svg";
import { ReactComponent as CompareCrossSolid } from "../../Assets/Icons/CompareCrossSolid.svg";
import { ReactComponent as PartnerLocation } from "../../Assets/Icons/PartnerLocation.svg";
import { useNavigate } from "react-router-dom";
import Info from "../Info";
import { useDispatch, useSelector } from "react-redux";
import { removeEmptyHospitals } from "../../Redux/compare/compare.actions";
import HospitalComparisonSearch from "../HospitalComparisonSearch";

const HospitalComparisonDropdown = ({
  options,
  selectedOption,
  setSelectedOption,
  removeHospital,
}) => {
  const { compareHospitals } = useSelector((state) => state.compare);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const optionsListRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [addHospitalFlag, setAddHospitalFlag] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  useEffect(() => {
    const isEmpty = Object.keys(selectedOption).length === 0;

    if (isEmpty && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 10,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
      setIsOpen(true);
      setAddHospitalFlag(true);
    }
  }, [selectedOption]);

  const toggleDropdown = () => {
    if (!isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 10,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setIsOpen(!isOpen);
    setIsSearchOpen(true);
  };

  const handleOptionClick = (option) => {
    const isAlreadySelected = compareHospitals.some(
      (hospital) => hospital.provider_number === option.provider_number,
    );
    
    if(isEmpty() && isAlreadySelected){
      setSelectedOption(option);
      return;
    }

    if(selectedOption.provider_number === option.provider_number || isAlreadySelected){
      setSelectedOption(option);
      return;
    }
    setSelectedOption(option);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        (!optionsListRef.current ||
          !optionsListRef.current.contains(event.target))
      ) {
        setIsOpen(false);
        setIsSearchOpen(false);
        setAddHospitalFlag(false);
        if (isEmpty()) {
          setTimeout(() => {
            dispatch(removeEmptyHospitals());
          }, 100);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleScroll = (event) => {
      // If the scroll happened inside the dropdown list, do nothing
      if (
        optionsListRef.current &&
        optionsListRef.current.contains(event.target)
      ) {
        return;
      }

      // Else, close the dropdown
      setIsOpen(false);
      if (isEmpty()) {
        dispatch(removeEmptyHospitals());
      }
    };

    window.addEventListener("scroll", handleScroll, true); // Capture scroll on any element

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const isEmpty = () => {
    return Object.keys(selectedOption).length === 0;
  };

  return (
    <Wrapper ref={dropdownRef} 
      layout
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 25,
      }}
    >
      <DropdownContainer
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        isEmpty={compareHospitals.length === 0}
      >
        {isEmpty() ? (
          <AddHospitalDiv 
            isScrollActive={compareHospitals.length >= 3}
            onClick={()=>{
              setAddHospitalFlag(true);
              toggleDropdown();
            }}>
              <SearchWrapper isVisible={searchTerm || addHospitalFlag}>
                <HospitalComparisonSearch
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                />
              </SearchWrapper>
          </AddHospitalDiv>
        ) : (
          <SelectedItem isSearchOpen={isSearchOpen}>
            {isSearchOpen && 
              <SearchWrapper isVisible={true}>
                <HospitalComparisonSearch
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                />
              </SearchWrapper>
            }
            {!isSearchOpen && <EllipsisContainer
              style={{ display: "flex", flexDirection: "column" }}
            >
              <Title
                onClick={() =>
                  navigate(`/hospitals/${selectedOption.provider_number}`)
                }
              >
                {Number(selectedOption?.esa) === 1 && (
                  <PartnerLocationIcon>
                    <PartnerLocation />
                  </PartnerLocationIcon>
                )}
                <span>{selectedOption?.label ?? "Select Option"}</span>

              </Title>
              {selectedOption?.location && (
                <SubText>{selectedOption?.location}</SubText>
              )}
            </EllipsisContainer>}
            {!isSearchOpen && <IconContainer>
              <Info
                showArrow={false}
                config={{
                  tooltipText: "Search Hospital",
                  position: "top",
                }}
              >
                <DropdownButton isHovered={isHovered} onClick={toggleDropdown}>
                  <DropdownSearch />
                </DropdownButton>
              </Info>
              <Info
                showArrow={false}
                config={{
                  tooltipText: "Remove Hospital",
                  position: "top",
                }}
              >
                <DropdownButton isHovered={true} onClick={removeHospital}>
                  <CompareCrossSolid />
                </DropdownButton>
              </Info>
            </IconContainer>}
          </SelectedItem>
        )}
        <HospitalSearchDropdown
          isOpen={Boolean(searchTerm || addHospitalFlag)}
          top={dropdownPosition.top}
          left={dropdownPosition.left}
          width={dropdownPosition.width}
        >
          <HospitalSearchList
            ref={optionsListRef}
            options={Object.keys(selectedOption).length === 0 ? options : []}
            onOptionClick={handleOptionClick}
            selectedOption={selectedOption}
            top={-0.1}
            isOpen={isOpen}
            customPadding={0.313}
            customBorderRadius={0.938}
            height={28.5}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        </HospitalSearchDropdown>
      </DropdownContainer>
    </Wrapper>
  );
};

export default HospitalComparisonDropdown;
